import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../../shared/providers/ref';
import { BookmarkState } from './bookmark-state';
import {
  BookmarkRepositoryProvider,
  SpatialReferenceRepositoryProvider,
} from '../../../../../shared/providers/repository-providers';
import { BookmarkEntity, LayerEntity, SpatialReferenceEntity } from '@gishub/domain';
import { BboxValueObject } from '@gishub/domain/models/value-objects/bbox-value-object';
import { LayerProvider } from '../layer-provider';
import { MapProvider } from '../../../components/map/map-provider';
import { MousePositionProvider } from '../../../components/mouse-position/mouse-position-provider';
import { BasemapSwitcherProvider } from '../../../components/basemap-switcher/basemap-switcher-provider';
import { AuthProvider } from '../../../../auth/shared/auth-provider';
import { ToastService } from '../../../../../services/toast-service';
import Extent from '@arcgis/core/geometry/Extent';

@Injectable()
export class BookmarkNotifier extends BaseNotifier<BookmarkState> {
  constructor(ref: Ref) {
    super(ref, { bookmarks: [], loading: false });
    this.repo.onSynced = () => {
      this.loadAll().catch(() => {});
    };
  }

  private get repo() {
    return this.ref.read(BookmarkRepositoryProvider).instance;
  }

  private get spatialReferenceRepo() {
    return this.ref.read(SpatialReferenceRepositoryProvider).instance;
  }

  private get authProvider() {
    return this.ref.read(AuthProvider);
  }

  private get basemapSwitcherProvider() {
    return this.ref.read(BasemapSwitcherProvider);
  }

  private get layerProvider() {
    return this.ref.read(LayerProvider);
  }

  private get mapProvider() {
    return this.ref.read(MapProvider);
  }

  private get mousePositionProvider() {
    return this.ref.read(MousePositionProvider);
  }

  override async build(): Promise<void> {
    await this.loadAll();
  }

  async loadAll(): Promise<void> {
    this.updateState(s => ({ ...s, loading: true }));
    try {
      const res = await this.repo.getAll(this.authProvider.state().username);
      this.setState({ bookmarks: res.data ?? [], loading: false });
    } catch {
      this.updateState(s => ({ ...s, loading: false }));
    }
  }

  async save(name: string): Promise<void> {
    const view = this.mapProvider.state().view;
    if (!view) {
      this.ref.watch(ToastService).warn('Marcadores', 'El mapa aún no está listo para guardar');
      return;
    }

    const { xmin, ymin, xmax, ymax } = view.extent;
    const srid = view.spatialReference.wkid ?? 4326;

    const bbox = new BboxValueObject({ xmin, ymin, xmax, ymax, srid });

    const projection = this.mousePositionProvider.state().projection;

    const entity = new BookmarkEntity({
      name,
      bbox,
      spatialReference: new SpatialReferenceEntity({
        wkid: this.spatialReferenceRepo.getWkidByProjection(projection),
      }),
      layers: this.captureActiveLayers(),
      baseMap: new LayerEntity({ name: this.basemapSwitcherProvider.state().activeId }),
    });

    try {
      const result = await this.repo.create(entity);
      if (!result.success) {
        this.ref.watch(ToastService).error('Marcadores', result.message ?? 'No se pudo guardar el marcador');
        return;
      }
    } catch (e) {
      this.handleError('guardar', e);
      return;
    }
    await this.loadAll();
  }

  async rename(bm: BookmarkEntity, newName: string): Promise<void> {
    const trimmed = newName.trim();
    if (!trimmed) return;

    const view = this.mapProvider.state().view;
    if (!view) {
      this.ref.watch(ToastService).warn('Marcadores', 'El mapa aún no está listo para actualizar');
      return;
    }

    const { xmin, ymin, xmax, ymax } = view.extent;
    const srid = view.spatialReference.wkid ?? 4326;
    const projection = this.mousePositionProvider.state().projection;

    const updated = new BookmarkEntity({
      ...bm,
      name: trimmed,
      bbox: new BboxValueObject({ xmin, ymin, xmax, ymax, srid }),
      spatialReference: new SpatialReferenceEntity({
        wkid: this.spatialReferenceRepo.getWkidByProjection(projection),
      }),
      layers: this.captureActiveLayers(),
      baseMap: new LayerEntity({ name: this.basemapSwitcherProvider.state().activeId }),
    });

    try {
      const result = await this.repo.update(updated);
      if (!result.success) {
        this.ref.watch(ToastService).error('Marcadores', result.message ?? 'No se pudo actualizar el marcador');
        return;
      }
    } catch (e) {
      this.handleError('actualizar', e);
      return;
    }
    await this.loadAll();
  }

  async remove(bm: BookmarkEntity): Promise<void> {
    try {
      const result = await this.repo.delete(bm);
      if (!result.success) {
        this.ref.watch(ToastService).error('Marcadores', result.message ?? 'No se pudo eliminar el marcador');
        return;
      }
    } catch (e) {
      this.handleError('eliminar', e);
      return;
    }
    await this.loadAll();
  }

  private handleError(action: string, e: unknown): void {
    const message = e instanceof Error && e.message ? e.message : `No se pudo ${action} el marcador`;
    this.ref.watch(ToastService).error('Marcadores', message);
  }

  async select(bm: BookmarkEntity): Promise<void> {
    this.mousePositionProvider.notifier.setProjection(
      this.spatialReferenceRepo.getProjectionByWkid(bm.spatialReference?.wkid ?? 4326),
    );
    if (bm.baseMap?.name) {
      this.basemapSwitcherProvider.notifier.setActive(bm.baseMap.name);
    }
    await this.restoreLayers(bm.layers);
    this.navigateToExtent(bm);
  }

  private captureActiveLayers(): LayerEntity[] {
    const captured: LayerEntity[] = [];
    const walk = (nodes: LayerEntity[]): void => {
      for (const node of nodes) {
        if (node.layers) {
          walk(node.layers);
        } else if (node.visible) {
          captured.push(new LayerEntity({ id: node.id, name: node.name, visible: node.visible, opacity: node.opacity }));
        }
      }
    };
    walk(this.layerProvider.state().layers);
    return captured;
  }

  private restoreLayers(savedLayers: LayerEntity[]): void {
    const notifier = this.layerProvider.notifier;
    const savedById = new Map(savedLayers.map(s => [s.id, s]));
    const walk = (nodes: LayerEntity[]): void => {
      for (const node of nodes) {
        if (node.layers) {
          walk(node.layers);
          continue;
        }
        const saved = savedById.get(node.id);
        if (saved) {
          if (node.visible !== saved.visible) {
            notifier.toggleLayerVisibility(node.id, saved.visible);
          }
          if (node.opacity !== saved.opacity) {
            notifier.setLayerOpacity(node.id, saved.opacity);
          }
        } else if (node.visible) {
          notifier.toggleLayerVisibility(node.id, false);
        }
      }
    };
    walk(this.layerProvider.state().layers);
  }

  private navigateToExtent(bm: BookmarkEntity): void {
    const view = this.mapProvider.state().view;
    if (!view) return;

    const { xmin, ymin, xmax, ymax, srid } = bm.bbox;
    const target = new Extent({ xmin, ymin, xmax, ymax, spatialReference: { wkid: srid } });
    view.goTo(target).catch(() => {});
  }
}
